import type {
  FilterParams,
  SearchParams,
} from '@/modules/common/context/QueryParams/models';
import type {
  DeviceType,
  EventAction,
  EventActionCreator,
  EventHandler,
  FilterParams as GtmFilterParams,
  ScreenOrientation,
} from '@/redux/middleware/googleTagManager/types';
import type {Page} from '@/types';

import {getCurrentUserId} from '@travel-frontend/user-id';

import {TravelTypes} from '@/modules/common/context/QueryParams/models';
import {EVENT_PAGE_LOAD} from '@/redux/middleware/googleTagManager/actionTypes';
import {
  getDeviceType,
  getOperatingSystem,
  getScreenOrientation,
} from '@/redux/middleware/googleTagManager/utils/environment';
import {mapFilterParamsToGTMEvent} from '@/redux/middleware/googleTagManager/utils/mapFilterParams';
import {buildVirtPath} from '@/redux/middleware/googleTagManager/utils/virtPath';

type Payload = {
  page: Page;
  defaultSearchParams: Partial<SearchParams>;
  searchParams: SearchParams;
  filterParams: FilterParams;
};

type Action = EventAction<Payload>;

type Event = GtmFilterParams & {
  virtpath: string;
  global_environment: string;
  // 'global_version': '<releaseversionkey>',
  global_osname: string;
  global_devicetype: DeviceType;
  global_userid: string;
  screen_orientation: ScreenOrientation | string;
  search_startdate: string;
  search_enddate: string;
  search_duration_min: string;
  search_duration_max: string;
  term: string;
  search_destination_id: string;
  search_departure_codes: string;
  search_number_of_adults: string;
  search_number_of_children: string;
  search_child_ages: string;
  search_form: string;
};

export const action: EventActionCreator<Action, Payload> = (payload) => ({
  type: EVENT_PAGE_LOAD,
  payload,
});

// @ts-expect-error ...
const serializePrimitive = (value, defaultValue): string => {
  const valueSerialized = String(value || '');
  const defaultValueSerialized = String(defaultValue || '');

  return (
    valueSerialized +
    (valueSerialized && valueSerialized === defaultValueSerialized ? '_d' : '')
  );
};

const serializeList = (
  value?: null | Array<any>,
  defaultValue?: null | Array<any>
): string => {
  return (
    value
      ?.map(
        (v) => v + (defaultValue && defaultValue?.indexOf(v) !== -1 ? '_d' : '')
      )
      .join(',') || ''
  );
};

export const handler: EventHandler<Action, Event> = (eventAction) => {
  const {page, defaultSearchParams, searchParams, filterParams} =
    eventAction.payload;
  const travelType = searchParams.travelType;

  return {
    virtpath: buildVirtPath({
      page,
      travelType,
    }),
    global_environment: import.meta.env.MODE,
    global_osname: getOperatingSystem(),
    global_devicetype: getDeviceType(),
    global_userid: getCurrentUserId(),
    screen_orientation: getScreenOrientation() || '',
    // Can't provide this on load as it's lazily resolved.
    // It also isn't static, since it's locale dependent.
    term: '',
    search_startdate: serializePrimitive(
      searchParams?.dateMin,
      defaultSearchParams?.dateMin
    ),
    search_enddate: serializePrimitive(
      searchParams?.dateMax,
      defaultSearchParams?.dateMax
    ),
    search_duration_min: serializePrimitive(
      searchParams?.durationMin,
      defaultSearchParams?.durationMin
    ),
    search_duration_max: serializePrimitive(
      searchParams?.durationMax,
      defaultSearchParams?.durationMax
    ),
    search_destination_id: serializePrimitive(
      searchParams?.destinationId,
      defaultSearchParams?.destinationId
    ),
    search_departure_codes: serializeList(
      searchParams?.airports,
      defaultSearchParams?.airports
    ),
    search_number_of_adults: serializePrimitive(
      searchParams?.rooms?.adultsCount,
      defaultSearchParams?.rooms?.adultsCount
    ),
    search_number_of_children: serializePrimitive(
      searchParams.rooms?.childrenCount,
      defaultSearchParams.rooms?.childrenCount
    ),
    search_child_ages: serializeList(
      searchParams?.rooms?.childrenAges,
      defaultSearchParams?.rooms?.childrenAges
    ),
    search_form: travelType === TravelTypes.packageTour ? 'pt' : 'oa',
    ...(filterParams && mapFilterParamsToGTMEvent(filterParams)),
    search_number_of_rooms: serializePrimitive(
      searchParams.rooms?.length,
      defaultSearchParams.rooms?.length
    ),
  };
};
